<script>
	// 수집 대시보드 (공유·통합) — 봇 미니페이지 "수집"(답변+개인정보)과 AI챗 "폼 응답"을 한 컴포넌트로.
	// 봇 빌더 "수집" 탭 / Main 콘솔 "수집" 섹션 / (선택) ChatBuilder "응답" 탭이 같은 컴포넌트를
	// source(bot|chat)+id만 바꿔 렌더한다. 유지보수 지점 1곳.
	//   source="chat" → getBlockList/getFormSubmissions (폼 제출 대시보드, ResponseCard)
	//   source="bot"  → getCollectionList (질문별 답변 아코디언 + 개인정보 표, 삭제 플로우)
	// 디자인: shadcn(--sb-*) 토큰. 추이 차트는 공용 MiniChart(단일 시리즈 막대) 재사용.
	import { PRConnect } from "@projectroom/ui-lib";
	import moment from "moment-timezone";
	import ResponseCard from "../chat-builder/ResponseCard.svelte";
	import MiniChart from "./MiniChart.svelte";

	export let source = "bot"; // bot | chat
	export let botId = null;
	export let chatId = null;
	export let active = true; // 표시/로딩 여부(부모가 탭 가시성으로 제어)
	export let embedded = false; // Main 콘솔에 임베드 시 내부 제목(h3) 숨김(섹션 헤더와 중복 방지)

	let loading = false;
	let loadedKey = null;

	// ── 공용 유틸 ──
	const firstLine = (s) => (s || "").split("\n")[0].trim();
	const parseDate = (v) => {
		const d = new Date(String(v ?? "").replace(" ", "T"));
		return isNaN(d.getTime()) ? null : d;
	};
	const mLocal = (v) => {
		const m = moment.utc(v);
		return m.isValid() ? m.local().toDate() : null;
	};
	const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
	const isToday = (d) => d && dayKey(d) === dayKey(new Date());
	const pct = (v, max) => `${Math.round((v / Math.max(1, max)) * 100)}%`;
	const fmt = (n) => (n || 0).toLocaleString();

	// ── 로드 디스패치(프로젝트 전환 대응) ──
	$: idKey = source === "chat" ? chatId : botId;
	$: if (active && idKey && `${source}:${idKey}` !== loadedKey) {
		loadedKey = `${source}:${idKey}`;
		source === "chat" ? loadChat() : loadBot();
	}

	/* =========================================================================
	 * CHAT — 폼 응답 (FormSubmissionsContext 이식)
	 * ======================================================================= */
	const CAP = 500;
	let blocks = []; // [{id,label,selectFields:[{key,options}]}]
	let selectedBlockId = "";
	let rows = [];
	let total = 0;
	let tablePage = 1;
	const tablePageSize = 20;

	const selectFieldsOf = (b) => {
		const form = (b.contents || []).find((c) => c.type === "form");
		return ((form && form.data && form.data.fields) || [])
			.filter((f) => f.type === "select" && f.key)
			.map((f) => ({ key: f.key, options: Array.isArray(f.options) ? f.options : [] }));
	};
	const blockLabel = (id) => (blocks.find((b) => b.id === id) || {}).label || id;

	const loadChat = () => {
		if (!chatId) return;
		loading = true;
		PRConnect.send("/api/Chat/getBlockList", { chatId }, (data) => {
			if (data.header.status === "success") {
				blocks = (data.result || [])
					.filter((b) => (b.contents || []).some((c) => c.type === "form"))
					.map((b) => ({ id: b.id, label: firstLine(b.description) || b.id, selectFields: selectFieldsOf(b) }));
			}
		});
		loadChatRows();
	};
	const loadChatRows = () => {
		if (!chatId) return;
		loading = true;
		PRConnect.send(
			"/api/Chat/getFormSubmissions",
			{ chatId, blockId: selectedBlockId || undefined, page: 1, pageSize: CAP },
			(data) => {
				loading = false;
				if (data.header.status !== "success") return;
				const r = data.result || {};
				rows = r.list || [];
				total = r.total || 0;
				tablePage = 1;
			},
		);
	};
	const changeBlock = (e) => {
		selectedBlockId = e.target.value;
		loadChatRows();
	};

	// 파생(chat)
	$: capped = total > rows.length;
	$: chatToday = rows.reduce((n, r) => (isToday(parseDate(r.createdAt)) ? n + 1 : n), 0);
	$: chatWeek = (() => {
		const now = new Date();
		const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6).getTime();
		return rows.reduce((n, r) => {
			const d = parseDate(r.createdAt);
			return d && d.getTime() >= start ? n + 1 : n;
		}, 0);
	})();
	$: byDay = (() => {
		const map = {};
		for (const r of rows) {
			const d = parseDate(r.createdAt);
			if (d) map[dayKey(d)] = (map[dayKey(d)] || 0) + 1;
		}
		const out = [];
		const now = new Date();
		for (let i = 13; i >= 0; i--) {
			const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
			out.push({ label: `${d.getMonth() + 1}/${d.getDate()}`, value: map[dayKey(d)] || 0 });
		}
		return out;
	})();
	$: selectedBlock = blocks.find((b) => b.id === selectedBlockId) || null;
	$: byForm = selectedBlockId
		? []
		: (() => {
				const map = {};
				for (const r of rows) map[r.blockId] = (map[r.blockId] || 0) + 1;
				return Object.entries(map)
					.map(([id, value]) => ({ label: blockLabel(id), value }))
					.sort((a, b) => b.value - a.value);
			})();
	$: byFormMax = Math.max(1, ...byForm.map((d) => d.value));
	$: distributions = !selectedBlock
		? []
		: selectedBlock.selectFields.map((f) => {
				const counts = {};
				for (const o of f.options) counts[o] = 0;
				for (const r of rows) {
					const v = (r.values || {})[f.key];
					if (v == null || String(v).trim() === "") continue;
					counts[v] = (counts[v] || 0) + 1;
				}
				const items = Object.entries(counts)
					.map(([label, value]) => ({ label, value }))
					.sort((a, b) => b.value - a.value);
				return { key: f.key, items, max: Math.max(1, ...items.map((i) => i.value)) };
			});
	$: tableTotalPages = Math.max(1, Math.ceil(rows.length / tablePageSize));
	$: tableRows = rows.slice((tablePage - 1) * tablePageSize, tablePage * tablePageSize);
	const goTable = (p) => {
		if (p < 1 || p > tableTotalPages) return;
		tablePage = p;
	};

	/* =========================================================================
	 * BOT — 수집(답변 + 개인정보) (CustomerResponse 이식·재스타일)
	 * ======================================================================= */
	let questionList = [];
	let privacyList = [];
	let botTab = "response"; // response | privacy
	let openQ = new Set(); // 펼친 질문 id
	let selQ = new Set(); // 선택된 질문 id(일괄삭제)
	let selP = new Set(); // 선택된 개인정보 id
	let privacyView = null; // {name,email,phone}
	let confirmState = null; // {title, desc, onOk}

	const loadBot = () => {
		if (!botId) return;
		loading = true;
		PRConnect.send("/api/Bot/getCollectionList", { botId, type: "INPUT" }, (data) => {
			loading = false;
			if (data.header.status !== "success") return;
			questionList = data.result.questionList || [];
			privacyList = data.result.privacyList || [];
			openQ = new Set();
			selQ = new Set();
			selP = new Set();
		});
	};

	const toggleOpen = (id) => {
		openQ.has(id) ? openQ.delete(id) : openQ.add(id);
		openQ = openQ;
	};
	const toggleSelQ = (id) => {
		selQ.has(id) ? selQ.delete(id) : selQ.add(id);
		selQ = selQ;
	};
	const toggleSelP = (id) => {
		selP.has(id) ? selP.delete(id) : selP.add(id);
		selP = selP;
	};

	const askConfirm = (title, desc, onOk) => (confirmState = { title, desc, onOk });
	const doConfirm = () => {
		const ok = confirmState?.onOk;
		confirmState = null;
		ok && ok();
	};

	const deleteQuestions = (ids) =>
		PRConnect.send("/api/Bot/deleteAnswerList", { botId, targetList: ids }, (d) => {
			if (d.header.status === "success") loadBot();
		});
	const deleteAnswer = (answerId) =>
		PRConnect.send("/api/Bot/deleteAnswer", { botId, answerId }, (d) => {
			if (d.header.status === "success") loadBot();
		});
	const deletePrivacy = (ids) =>
		PRConnect.send("/api/Bot/deletePrivacyList", { targetList: ids }, (d) => {
			if (d.header.status === "success") loadBot();
		});

	const bulkDeleteQuestions = () => {
		if (!selQ.size) return;
		const ids = [...selQ];
		const answers = questionList.filter((q) => selQ.has(q.id)).reduce((n, q) => n + (q.answerList?.length || 0), 0);
		askConfirm(`선택한 질문 ${ids.length}건과 답변 ${answers}건이 삭제됩니다.`, "삭제한 데이터는 복구할 수 없어요.", () => deleteQuestions(ids));
	};
	const bulkDeletePrivacy = () => {
		if (!selP.size) return;
		const ids = [...selP];
		askConfirm(`선택한 개인정보 ${ids.length}건이 삭제됩니다.`, "삭제한 데이터는 복구할 수 없어요.", () => deletePrivacy(ids));
	};
	const askDeleteAnswer = (answerId) =>
		askConfirm("답변 1건이 삭제됩니다.", "삭제한 데이터는 복구할 수 없어요.", () => deleteAnswer(answerId));

	// 개인정보 상세
	const fmtPhone = (raw) => {
		const digits = (raw || "").replace(/\D/g, "");
		const m = digits.match(/^(\d{2,3})(\d{3,4})(\d{4})$/);
		return m ? `${m[1]}-${m[2]}-${m[3]}` : raw || "-";
	};
	const parseContent = (c) => {
		try {
			return JSON.parse(c) || {};
		} catch {
			return {};
		}
	};
	const openPrivacyByUuid = (uuid) =>
		PRConnect.send("/api/Bot/getPrivacy", { uuid }, (d) => {
			if (d.header.status !== "success") return;
			const p = parseContent(d.result.content);
			privacyView = { name: p.name || "-", email: p.email || "-", phone: fmtPhone(p.phone) };
		});
	const openPrivacyRow = (privacy) => {
		const p = parseContent(privacy.content);
		privacyView = { name: p.name || "-", email: p.email || "-", phone: fmtPhone(p.phone) };
	};
	const dateFmt = (v) => {
		const d = mLocal(v);
		return d ? moment(d).format("YYYY.MM.DD") : "-";
	};

	// 파생(bot)
	$: totalQuestions = questionList.length;
	$: totalAnswers = questionList.reduce((n, q) => n + (q.answerList?.length || 0), 0);
	$: totalPrivacy = privacyList.length;
	$: botToday =
		questionList.reduce((n, q) => n + (q.answerList || []).filter((a) => isToday(mLocal(a.creationDate))).length, 0) +
		privacyList.filter((p) => isToday(mLocal(p.creationDate))).length;
	$: byDayBot = (() => {
		const map = {};
		const add = (v) => {
			const d = mLocal(v);
			if (d) map[dayKey(d)] = (map[dayKey(d)] || 0) + 1;
		};
		for (const q of questionList) for (const a of q.answerList || []) add(a.creationDate);
		for (const p of privacyList) add(p.creationDate);
		const out = [];
		const now = new Date();
		for (let i = 13; i >= 0; i--) {
			const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
			out.push({ label: `${d.getMonth() + 1}/${d.getDate()}`, value: map[dayKey(d)] || 0 });
		}
		return out;
	})();
	$: hasBotData = totalQuestions > 0 || totalPrivacy > 0;

	const refresh = () => (source === "chat" ? loadChat() : loadBot());
</script>

<div class="coll-dash tw-font-sans tw-text-foreground">
	<div class="head">
		<div class="head-l">
			{#if !embedded}<h3>수집</h3>{/if}
			<span class="pill">{source === "chat" ? "폼 응답" : "고객 수집"}</span>
		</div>
		{#if idKey}
			<button type="button" class="mini" on:click={refresh} disabled={loading}>새로고침</button>
		{/if}
	</div>

	{#if !idKey}
		<div class="empty">{source === "chat" ? "챗을 선택하면 폼 응답을 볼 수 있어요." : "봇을 선택하면 수집된 데이터를 볼 수 있어요."}</div>

	<!-- ══════════════ CHAT ══════════════ -->
	{:else if source === "chat"}
		<p class="desc">입력폼 콘텐츠에서 고객이 <b>[제출]</b>한 값이 여기 쌓여요.</p>
		{#if blocks.length === 0 && total === 0}
			<div class="empty">
				아직 폼 응답이 없어요.<br />
				<b>블록</b> 탭에서 <b>콘텐츠 추가 &gt; 입력폼</b>으로 폼을 만들면, 고객이 제출한 데이터가 여기 모입니다.
			</div>
		{:else}
			<div class="toolbar">
				<select class="sel" value={selectedBlockId} on:change={changeBlock}>
					<option value="">전체 폼</option>
					{#each blocks as b}
						<option value={b.id}>{b.label}</option>
					{/each}
				</select>
			</div>

			<div class="stats">
				<div class="stat"><div class="stat-n">{fmt(total)}</div><div class="stat-l">총 응답</div></div>
				<div class="stat"><div class="stat-n">{fmt(chatToday)}</div><div class="stat-l">오늘</div></div>
				<div class="stat"><div class="stat-n">{fmt(chatWeek)}</div><div class="stat-l">최근 7일</div></div>
				<div class="stat"><div class="stat-n">{selectedBlockId ? 1 : blocks.length}</div><div class="stat-l">{selectedBlockId ? "선택한 폼" : "폼 개수"}</div></div>
			</div>

			<MiniChart title="제출 추이" subtitle={`최근 14일${capped ? ` · 최근 ${rows.length}건 기준` : ""}`} kind="bar" series={byDay} />

			{#if !selectedBlockId}
				{#if byForm.length}
					<div class="card">
						<div class="card-h"><span class="card-t">폼별 응답 수</span></div>
						<div class="hbars">
							{#each byForm as it}
								<div class="hbar-row">
									<div class="hbar-l" title={it.label}>{it.label}</div>
									<div class="hbar-track"><div class="hbar-fill" style="width:{pct(it.value, byFormMax)}"></div></div>
									<div class="hbar-v">{it.value}</div>
								</div>
							{/each}
						</div>
					</div>
				{/if}
			{:else if distributions.length}
				{#each distributions as dist}
					<div class="card">
						<div class="card-h"><span class="card-t">{dist.key}</span><span class="card-s">선택형 분포</span></div>
						<div class="hbars">
							{#each dist.items as it}
								<div class="hbar-row">
									<div class="hbar-l" title={it.label}>{it.label}</div>
									<div class="hbar-track"><div class="hbar-fill" style="width:{pct(it.value, dist.max)}"></div></div>
									<div class="hbar-v">{it.value}</div>
								</div>
							{/each}
						</div>
					</div>
				{/each}
			{/if}

			<div class="card">
				<div class="card-h">
					<span class="card-t">개별 응답</span>
					<span class="card-s">{rows.length}건{capped ? ` (전체 ${total}건 중 최근 ${rows.length})` : ""}</span>
				</div>
				{#if tableRows.length === 0}
					<div class="empty small">{loading ? "불러오는 중…" : "해당 조건의 응답이 없어요."}</div>
				{:else}
					<div class="rc-list">
						{#each tableRows as row (row.id)}
							<ResponseCard {row} showForm={!selectedBlockId} formLabel={blockLabel(row.blockId)} />
						{/each}
					</div>
					{#if tableTotalPages > 1}
						<div class="pager">
							<button type="button" class="mini" on:click={() => goTable(tablePage - 1)} disabled={tablePage <= 1}>이전</button>
							<span class="pageinfo">{tablePage} / {tableTotalPages}</span>
							<button type="button" class="mini" on:click={() => goTable(tablePage + 1)} disabled={tablePage >= tableTotalPages}>다음</button>
						</div>
					{/if}
				{/if}
			</div>
		{/if}

	<!-- ══════════════ BOT ══════════════ -->
	{:else}
		<p class="desc">고정 콘텐츠의 <b>입력/개인정보</b> 버튼으로 고객이 남긴 답변과 개인정보가 여기 모여요.</p>
		{#if !hasBotData && !loading}
			<div class="empty">아직 수집된 데이터가 없어요.</div>
		{:else}
			<div class="stats">
				<div class="stat"><div class="stat-n">{fmt(totalAnswers)}</div><div class="stat-l">총 답변</div></div>
				<div class="stat"><div class="stat-n">{fmt(totalQuestions)}</div><div class="stat-l">질문 수</div></div>
				<div class="stat"><div class="stat-n">{fmt(totalPrivacy)}</div><div class="stat-l">개인정보</div></div>
				<div class="stat"><div class="stat-n">{fmt(botToday)}</div><div class="stat-l">오늘</div></div>
			</div>

			<MiniChart title="수집 추이" subtitle="최근 14일" kind="bar" series={byDayBot} />

			<!-- 답변 / 개인정보 서브탭 -->
			<div class="subtabs">
				<button type="button" class:on={botTab === "response"} on:click={() => (botTab = "response")}>답변 <span class="cnt">{totalAnswers}</span></button>
				<button type="button" class:on={botTab === "privacy"} on:click={() => (botTab = "privacy")}>개인정보 <span class="cnt">{totalPrivacy}</span></button>
			</div>

			{#if botTab === "response"}
				<div class="card">
					<div class="card-h">
						<span class="card-t">수집된 답변</span>
						<span class="spacer" />
						{#if selQ.size}
							<button type="button" class="mini danger" on:click={bulkDeleteQuestions}>선택 삭제 {selQ.size}</button>
						{/if}
					</div>
					{#if questionList.length === 0}
						<div class="empty small">{loading ? "불러오는 중…" : "수집된 답변이 없어요."}</div>
					{:else}
						<div class="q-list">
							{#each questionList as q (q.id)}
								<div class="q-item">
									<div class="q-head">
										<button type="button" class="chk" class:on={selQ.has(q.id)} on:click={() => toggleSelQ(q.id)} aria-label="선택"></button>
										<button type="button" class="q-main" on:click={() => toggleOpen(q.id)}>
											<span class="q-content">{q.content}</span>
											<span class="q-meta">답변 {q.answerList?.length || 0}건</span>
											<svg class="q-caret" class:open={openQ.has(q.id)} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>
										</button>
									</div>
									{#if openQ.has(q.id)}
										<div class="a-list">
											{#each q.answerList || [] as answer (answer.id)}
												<div class="a-item">
													<span class="a-content">{answer.content}</span>
													<span class="a-spacer" />
													{#if answer.privacy === "true"}
														<button type="button" class="mini" on:click={() => openPrivacyByUuid(answer.uuid)}>개인정보 보기</button>
													{/if}
													<span class="a-date">{dateFmt(answer.creationDate)}</span>
													<button type="button" class="a-del" on:click={() => askDeleteAnswer(answer.id)} aria-label="삭제">✕</button>
												</div>
											{/each}
										</div>
									{/if}
								</div>
							{/each}
						</div>
					{/if}
				</div>
			{:else}
				<div class="card">
					<div class="card-h">
						<span class="card-t">수집된 개인정보</span>
						<span class="spacer" />
						{#if selP.size}
							<button type="button" class="mini danger" on:click={bulkDeletePrivacy}>선택 삭제 {selP.size}</button>
						{/if}
					</div>
					{#if privacyList.length === 0}
						<div class="empty small">{loading ? "불러오는 중…" : "수집된 개인정보가 없어요."}</div>
					{:else}
						<div class="p-head">
							<span class="p-chk-sp" />
							<span>이름</span><span>이메일</span><span>전화번호</span><span>제출일</span>
						</div>
						<div class="p-list">
							{#each privacyList as privacy (privacy.id)}
								{@const c = parseContent(privacy.content)}
								<div class="p-row">
									<button type="button" class="chk" class:on={selP.has(privacy.id)} on:click={() => toggleSelP(privacy.id)} aria-label="선택"></button>
									<span class="p-cell">{c.name || "-"}</span>
									<span class="p-cell">{c.email || "-"}</span>
									<button type="button" class="p-view" on:click={() => openPrivacyRow(privacy)}>확인하기</button>
									<span class="p-cell">{dateFmt(privacy.creationDate)}</span>
								</div>
							{/each}
						</div>
					{/if}
				</div>
			{/if}
		{/if}
	{/if}
</div>

<!-- 개인정보 상세 팝업 -->
{#if privacyView}
	<div class="modal-mask" on:click={() => (privacyView = null)}>
		<div class="modal" on:click|stopPropagation>
			<div class="modal-t">개인정보</div>
			<div class="pv-row"><span class="pv-k">이름</span><span class="pv-v">{privacyView.name}</span></div>
			<div class="pv-row"><span class="pv-k">이메일</span><span class="pv-v">{privacyView.email}</span></div>
			<div class="pv-row"><span class="pv-k">전화번호</span><span class="pv-v">{privacyView.phone}</span></div>
			<div class="modal-foot">
				<button type="button" class="btn primary" on:click={() => (privacyView = null)}>확인</button>
			</div>
		</div>
	</div>
{/if}

<!-- 삭제 확인 모달 -->
{#if confirmState}
	<div class="modal-mask" on:click={() => (confirmState = null)}>
		<div class="modal" on:click|stopPropagation>
			<div class="modal-t">{confirmState.title}</div>
			<p class="modal-d">{confirmState.desc}</p>
			<div class="modal-foot">
				<button type="button" class="btn" on:click={() => (confirmState = null)}>취소</button>
				<button type="button" class="btn danger" on:click={doConfirm}>삭제하기</button>
			</div>
		</div>
	</div>
{/if}

<style>
	.coll-dash {
		padding: 10px;
	}
	.head {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
		margin-bottom: 4px;
	}
	.head-l {
		display: flex;
		align-items: center;
		gap: 8px;
	}
	.head h3 {
		margin: 0;
		font-size: 15px;
		font-weight: 600;
		color: hsl(var(--sb-foreground));
	}
	.pill {
		border-radius: 999px;
		background: hsl(var(--sb-muted));
		padding: 2px 8px;
		font-size: 12px;
		color: hsl(var(--sb-muted-foreground));
	}
	.desc {
		margin: 0 0 14px;
		font-size: 12px;
		color: hsl(var(--sb-muted-foreground));
	}
	.empty {
		padding: 28px 16px;
		text-align: center;
		font-size: 13px;
		line-height: 1.7;
		color: hsl(var(--sb-muted-foreground));
		border: 1px dashed hsl(var(--sb-border));
		border-radius: 8px;
		background: hsl(var(--sb-muted) / 0.3);
	}
	.empty.small {
		padding: 18px;
	}
	.toolbar {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 12px;
	}
	.sel {
		padding: 6px 8px;
		border: 1px solid hsl(var(--sb-border));
		border-radius: 6px;
		font-size: 13px;
		font-family: inherit;
		color: hsl(var(--sb-foreground));
		background: hsl(var(--sb-card));
		max-width: 220px;
	}
	.spacer {
		flex: 1;
	}
	.mini {
		padding: 4px 10px;
		font-size: 12px;
		background: hsl(var(--sb-muted));
		color: hsl(var(--sb-primary));
		border: 1px solid hsl(var(--sb-border));
		border-radius: 6px;
		cursor: pointer;
	}
	.mini:disabled {
		opacity: 0.5;
		cursor: default;
	}
	.mini.danger {
		color: hsl(var(--sb-destructive));
	}

	/* stat 타일 */
	.stats {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: 8px;
		margin-bottom: 14px;
	}
	@media (max-width: 640px) {
		.stats {
			grid-template-columns: repeat(2, 1fr);
		}
	}
	.stat {
		border: 1px solid hsl(var(--sb-border));
		border-radius: 8px;
		padding: 10px 12px;
		background: hsl(var(--sb-card));
	}
	.stat-n {
		font-size: 20px;
		font-weight: 700;
		color: hsl(var(--sb-foreground));
		line-height: 1.1;
		font-variant-numeric: tabular-nums;
	}
	.stat-l {
		margin-top: 3px;
		font-size: 11px;
		color: hsl(var(--sb-muted-foreground));
	}

	/* 카드 */
	.card {
		border: 1px solid hsl(var(--sb-border));
		border-radius: 8px;
		padding: 12px;
		margin-top: 12px;
		background: hsl(var(--sb-card));
	}
	.card-h {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 10px;
	}
	.card-t {
		font-size: 13px;
		font-weight: 600;
		color: hsl(var(--sb-foreground));
	}
	.card-s {
		font-size: 11px;
		color: hsl(var(--sb-muted-foreground));
	}

	/* 가로 막대 */
	.hbars {
		display: flex;
		flex-direction: column;
		gap: 7px;
	}
	.hbar-row {
		display: grid;
		grid-template-columns: 96px 1fr 34px;
		align-items: center;
		gap: 8px;
	}
	.hbar-l {
		font-size: 12px;
		color: hsl(var(--sb-foreground));
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	.hbar-track {
		height: 14px;
		background: hsl(var(--sb-muted) / 0.6);
		border-radius: 4px;
		overflow: hidden;
	}
	.hbar-fill {
		height: 100%;
		min-width: 2px;
		background: hsl(var(--sb-primary));
		border-radius: 4px;
	}
	.hbar-v {
		font-size: 12px;
		font-weight: 600;
		color: hsl(var(--sb-muted-foreground));
		text-align: right;
	}

	/* 개별 응답 리스트 */
	.rc-list {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}
	.pager {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 12px;
		margin-top: 12px;
	}
	.pageinfo {
		font-size: 12px;
		color: hsl(var(--sb-muted-foreground));
	}

	/* ── BOT 서브탭 ── */
	.subtabs {
		display: inline-flex;
		gap: 4px;
		margin-top: 14px;
		padding: 3px;
		border: 1px solid hsl(var(--sb-border));
		border-radius: 9px;
		background: hsl(var(--sb-muted) / 0.4);
	}
	.subtabs button {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		border: none;
		background: transparent;
		padding: 5px 14px;
		font-size: 13px;
		font-weight: 600;
		color: hsl(var(--sb-muted-foreground));
		border-radius: 7px;
		cursor: pointer;
	}
	.subtabs button.on {
		background: hsl(var(--sb-card));
		color: hsl(var(--sb-primary));
		box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
	}
	.subtabs .cnt {
		font-size: 11px;
		font-variant-numeric: tabular-nums;
		padding: 0 6px;
		border-radius: 999px;
		background: hsl(var(--sb-muted));
		color: hsl(var(--sb-muted-foreground));
	}
	.subtabs button.on .cnt {
		background: hsl(var(--sb-primary) / 0.15);
		color: hsl(var(--sb-primary));
	}

	/* 체크박스 */
	.chk {
		width: 18px;
		height: 18px;
		flex-shrink: 0;
		border: 1.5px solid hsl(var(--sb-border));
		border-radius: 5px;
		background: hsl(var(--sb-card));
		cursor: pointer;
		position: relative;
	}
	.chk.on {
		background: hsl(var(--sb-primary));
		border-color: hsl(var(--sb-primary));
	}
	.chk.on::after {
		content: "";
		position: absolute;
		left: 5px;
		top: 1.5px;
		width: 4px;
		height: 9px;
		border: solid hsl(var(--sb-primary-foreground));
		border-width: 0 2px 2px 0;
		transform: rotate(45deg);
	}

	/* 답변 아코디언 */
	.q-list {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}
	.q-item {
		border: 1px solid hsl(var(--sb-border));
		border-radius: 8px;
		background: hsl(var(--sb-card));
		overflow: hidden;
	}
	.q-head {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 10px 12px;
	}
	.q-main {
		display: flex;
		align-items: center;
		gap: 10px;
		flex: 1;
		min-width: 0;
		border: none;
		background: transparent;
		cursor: pointer;
		padding: 0;
		text-align: left;
	}
	.q-content {
		flex: 1;
		min-width: 0;
		font-size: 13px;
		font-weight: 600;
		color: hsl(var(--sb-foreground));
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	.q-meta {
		font-size: 11px;
		color: hsl(var(--sb-muted-foreground));
		white-space: nowrap;
	}
	.q-caret {
		width: 16px;
		height: 16px;
		color: hsl(var(--sb-muted-foreground));
		transition: transform 0.2s;
		flex-shrink: 0;
	}
	.q-caret.open {
		transform: rotate(180deg);
	}
	.a-list {
		border-top: 1px solid hsl(var(--sb-border));
		background: hsl(var(--sb-muted) / 0.25);
		display: flex;
		flex-direction: column;
	}
	.a-item {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 9px 12px;
		border-top: 1px solid hsl(var(--sb-border) / 0.6);
	}
	.a-item:first-child {
		border-top: none;
	}
	.a-content {
		font-size: 13px;
		color: hsl(var(--sb-foreground));
		word-break: break-word;
	}
	.a-spacer {
		flex: 1;
	}
	.a-date {
		font-size: 11px;
		color: hsl(var(--sb-muted-foreground));
		white-space: nowrap;
		font-variant-numeric: tabular-nums;
	}
	.a-del {
		border: none;
		background: transparent;
		color: hsl(var(--sb-muted-foreground));
		cursor: pointer;
		font-size: 12px;
		padding: 2px 4px;
		border-radius: 4px;
	}
	.a-del:hover {
		color: hsl(var(--sb-destructive));
		background: hsl(var(--sb-destructive) / 0.1);
	}

	/* 개인정보 표 */
	.p-head,
	.p-row {
		display: grid;
		grid-template-columns: 18px 1fr 1.4fr 92px 96px;
		align-items: center;
		gap: 10px;
	}
	.p-head {
		padding: 0 12px 8px;
		font-size: 11px;
		font-weight: 600;
		color: hsl(var(--sb-muted-foreground));
	}
	.p-list {
		display: flex;
		flex-direction: column;
	}
	.p-row {
		padding: 10px 12px;
		border-top: 1px solid hsl(var(--sb-border));
	}
	.p-cell {
		font-size: 13px;
		color: hsl(var(--sb-foreground));
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		font-variant-numeric: tabular-nums;
	}
	.p-view {
		justify-self: start;
		padding: 4px 10px;
		font-size: 12px;
		background: hsl(var(--sb-muted));
		color: hsl(var(--sb-primary));
		border: 1px solid hsl(var(--sb-border));
		border-radius: 6px;
		cursor: pointer;
	}

	/* 모달(개인정보 상세 / 삭제 확인) */
	.modal-mask {
		position: fixed;
		inset: 0;
		z-index: 60;
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(16, 24, 40, 0.5);
	}
	.modal {
		width: min(360px, calc(100vw - 40px));
		background: hsl(var(--sb-card));
		border: 1px solid hsl(var(--sb-border));
		border-radius: 14px;
		padding: 20px;
		box-shadow: 0 20px 50px rgba(16, 24, 40, 0.28);
	}
	.modal-t {
		font-size: 15px;
		font-weight: 700;
		color: hsl(var(--sb-foreground));
		margin-bottom: 12px;
	}
	.modal-d {
		margin: 0 0 16px;
		font-size: 13px;
		color: hsl(var(--sb-muted-foreground));
	}
	.pv-row {
		display: flex;
		gap: 12px;
		padding: 8px 0;
		border-bottom: 1px solid hsl(var(--sb-border) / 0.6);
	}
	.pv-k {
		width: 72px;
		font-size: 12px;
		font-weight: 600;
		color: hsl(var(--sb-muted-foreground));
	}
	.pv-v {
		flex: 1;
		font-size: 13px;
		color: hsl(var(--sb-foreground));
		word-break: break-word;
	}
	.modal-foot {
		display: flex;
		justify-content: flex-end;
		gap: 8px;
		margin-top: 16px;
	}
	.btn {
		padding: 8px 16px;
		font-size: 13px;
		font-weight: 600;
		border-radius: 8px;
		border: 1px solid hsl(var(--sb-border));
		background: hsl(var(--sb-card));
		color: hsl(var(--sb-foreground));
		cursor: pointer;
	}
	.btn.primary {
		background: hsl(var(--sb-primary));
		border-color: hsl(var(--sb-primary));
		color: hsl(var(--sb-primary-foreground));
	}
	.btn.danger {
		background: hsl(var(--sb-destructive));
		border-color: hsl(var(--sb-destructive));
		color: hsl(var(--sb-destructive-foreground));
	}
</style>
